import { getUserEntitlements } from "../config/plans";

export const DEFAULT_EDITOR_PROFILE_THEME = "neon_arena";

export const EDITOR_PROFILE_THEMES = Object.freeze([
  {
    id: "neon_arena",
    name: "Neon Arena",
    accent: "#9146FF",
    secondary: "#84ff00",
    surface:
      "linear-gradient(135deg, rgba(145, 70, 255, 0.2) 0%, rgba(132, 255, 0, 0.12) 58%, rgba(7, 6, 13, 0.96) 100%)",
    glowA: "radial-gradient(circle, rgba(145, 70, 255, 0.55) 0%, transparent 65%)",
    glowB: "radial-gradient(circle, rgba(132, 255, 0, 0.34) 0%, transparent 62%)",
    ring: "linear-gradient(135deg, #9146FF, #84ff00)",
  },
  {
    id: "mint_focus",
    name: "Mint Focus",
    accent: "#84ff00",
    secondary: "#38bdf8",
    surface:
      "linear-gradient(135deg, rgba(132, 255, 0, 0.18) 0%, rgba(56, 189, 248, 0.14) 52%, rgba(7, 6, 13, 0.96) 100%)",
    glowA: "radial-gradient(circle, rgba(132, 255, 0, 0.44) 0%, transparent 62%)",
    glowB: "radial-gradient(circle, rgba(56, 189, 248, 0.3) 0%, transparent 62%)",
    ring: "linear-gradient(135deg, #84ff00, #38bdf8)",
  },
  {
    id: "midnight_pro",
    name: "Midnight Pro",
    accent: "#38bdf8",
    secondary: "#f59e0b",
    surface:
      "linear-gradient(135deg, rgba(56, 189, 248, 0.18) 0%, rgba(245, 158, 11, 0.12) 52%, rgba(7, 6, 13, 0.96) 100%)",
    glowA: "radial-gradient(circle, rgba(56, 189, 248, 0.42) 0%, transparent 62%)",
    glowB: "radial-gradient(circle, rgba(245, 158, 11, 0.24) 0%, transparent 62%)",
    ring: "linear-gradient(135deg, #38bdf8, #f59e0b)",
  },
]);

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function getNestedValue(source, path) {
  return path.split(".").reduce((current, key) => current?.[key], source);
}

function readNumericMetric(sources, keys) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      const value = key.includes(".") ? getNestedValue(source, key) : source[key];
      const numericValue =
        typeof value === "string" && value.trim() !== "" ? Number(value) : value;

      if (isFiniteNumber(numericValue)) {
        return numericValue;
      }
    }
  }

  return null;
}

function formatCount(value) {
  if (!isFiniteNumber(value)) return "Tracking soon";
  return value.toLocaleString();
}

function formatPercent(value) {
  if (!isFiniteNumber(value)) return "Tracking soon";
  return `${Math.round(value)}%`;
}

function readMetricWithOverrides(sources, keys, overrides = {}) {
  for (const key of keys) {
    const overrideValue = overrides[key];

    if (isFiniteNumber(overrideValue)) {
      return overrideValue;
    }
  }

  return readNumericMetric(sources, keys);
}

export function getEditorProfileTheme(themeId) {
  return (
    EDITOR_PROFILE_THEMES.find((theme) => theme.id === themeId) ||
    EDITOR_PROFILE_THEMES[0]
  );
}

export function sanitizeEditorProfileTheme(themeId) {
  return getEditorProfileTheme(themeId).id;
}

export function getActiveEditorProfileTheme(userData = {}) {
  const entitlements = getUserEntitlements(userData);

  if (!entitlements.premiumThemes) {
    return getEditorProfileTheme(DEFAULT_EDITOR_PROFILE_THEME);
  }

  return getEditorProfileTheme(userData?.profileInfo?.profileTheme);
}

export function getActiveEditorProfileBannerUrl(userData = {}) {
  const entitlements = getUserEntitlements(userData);

  if (!entitlements.customProfileBanner) {
    return "";
  }

  return userData?.profileInfo?.profileBannerUrl || "";
}

export function countEditorPortfolioVideos(videoLinks = {}) {
  return Object.values(videoLinks).filter((value) => String(value || "").trim()).length;
}

export function flattenEditorOrders(orders = {}) {
  if (!orders || typeof orders !== "object") return [];

  return Object.values(orders)
    .flatMap((statusOrders) =>
      statusOrders && typeof statusOrders === "object"
        ? Object.values(statusOrders)
        : []
    )
    .filter(Boolean);
}

export function buildEditorOrderStatistics(orders = {}) {
  const completedOrders = flattenEditorOrders(orders).filter((order) =>
    ["approved", "completed"].includes(String(order?.status || "").toLowerCase())
  );

  const creatorCounts = completedOrders.reduce((accumulator, order) => {
    const creatorId = order?.creatorId;

    if (!creatorId) return accumulator;

    accumulator[creatorId] = (accumulator[creatorId] || 0) + 1;
    return accumulator;
  }, {});

  const returningCreators = Object.values(creatorCounts).filter(
    (count) => count > 1
  ).length;

  return {
    ordersCompleted: completedOrders.length,
    returningCreators,
  };
}

export function sumEditorPortfolioVideoViews(videos = []) {
  return videos.reduce((total, video) => {
    const views = Number(video?.statistics?.viewCount || 0);
    return total + (Number.isFinite(views) ? views : 0);
  }, 0);
}

export function buildEditorConversationStatistics(chats = [], currentUserId = "") {
  if (!currentUserId) {
    return {
      messagesReceived: null,
      responseRate: null,
    };
  }

  let messagesReceived = 0;
  let conversationsWithIncoming = 0;
  let repliedConversations = 0;

  chats.forEach((chat) => {
    const messages = Array.isArray(chat?.messages) ? chat.messages : [];
    const incomingMessages = messages.filter(
      (message) => message?.senderId && message.senderId !== currentUserId
    );
    const outgoingMessages = messages.filter(
      (message) => message?.senderId === currentUserId
    );

    messagesReceived += incomingMessages.length;

    if (incomingMessages.length > 0) {
      conversationsWithIncoming += 1;
    }

    if (incomingMessages.length > 0 && outgoingMessages.length > 0) {
      repliedConversations += 1;
    }
  });

  return {
    messagesReceived,
    responseRate:
      conversationsWithIncoming > 0
        ? (repliedConversations / conversationsWithIncoming) * 100
        : null,
  };
}

export function buildEditorPortfolioStatistics(userData = {}, liveMetrics = {}) {
  const analyticsSources = [
    userData?.profileAnalytics,
    userData?.analytics,
    userData?.editorAnalytics,
    userData?.stats,
  ].filter(Boolean);

  const portfolioVideoCount = countEditorPortfolioVideos(userData?.videoLinks);
  const profileViews = readMetricWithOverrides(
    analyticsSources,
    ["profileViews", "views.profile"],
    liveMetrics
  );
  const portfolioVideoViews = readMetricWithOverrides(analyticsSources, [
    "portfolioVideoViews",
    "videoViews",
    "videos.views",
  ], liveMetrics);
  const messagesReceived = readMetricWithOverrides(analyticsSources, [
    "messagesReceived",
    "messages.received",
  ], liveMetrics);
  const ordersCompleted = readMetricWithOverrides(analyticsSources, [
    "ordersCompleted",
    "orders.completed",
    "completedOrders",
  ], liveMetrics);
  const returningCreators = readMetricWithOverrides(analyticsSources, [
    "returningCreators",
    "creators.returning",
  ], liveMetrics);
  const responseRate = readMetricWithOverrides(analyticsSources, [
    "responseRate",
    "messages.responseRate",
  ], liveMetrics);

  return [
    {
      label: "Profile views",
      value: formatCount(profileViews),
      isLive: profileViews !== null,
    },
    {
      label: "Portfolio video views",
      value: formatCount(portfolioVideoViews),
      isLive: portfolioVideoViews !== null,
    },
    {
      label: "Messages received",
      value: formatCount(messagesReceived),
      isLive: messagesReceived !== null,
    },
    {
      label: "Orders completed",
      value: formatCount(ordersCompleted),
      isLive: ordersCompleted !== null,
    },
    {
      label: "Returning creators",
      value: formatCount(returningCreators),
      isLive: returningCreators !== null,
    },
    {
      label: "Response rate",
      value: formatPercent(responseRate),
      isLive: responseRate !== null,
    },
    {
      label: "Portfolio videos live",
      value: formatCount(portfolioVideoCount),
      isLive: true,
    },
  ];
}

export function validateEditorProfileBannerFile(file) {
  if (!file) {
    return "Choose a banner image to upload.";
  }

  if (!String(file.type || "").startsWith("image/")) {
    return "Upload a JPG, PNG, WEBP, or GIF image.";
  }

  if (Number(file.size || 0) > 5 * 1024 * 1024) {
    return "Keep banner images under 5MB.";
  }

  return null;
}
